/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import cookieStorage from "../../cookieStorage";
import {
    Container,
    Col,
    Image,
    Row,
    Spinner,
    ProgressBar,
    Button
} from "react-bootstrap";
import { useBetween } from "use-between";
import { Link, useParams } from "react-router-dom";

import CartLayout from "../../layout/CartLayout";
import ProductImg from "../../assets/images/collection/product.png";
import { LeftArrow, RightArrow } from "../../components/icons";
import useApi from "../useApi";
import ProductSkeleton from "./ProductSkeleton";

import "./index.scss";
import { toast } from "react-toastify";
import { roundDecimals, toDateType, toTwoDecimals } from "../../plugins";
import AwareButton from "../../components/button";
import { customOperation } from "../../components/cart/item";

const SlickArrowLeft = ({ currentSlide, slideCount, ...props }) => (
    <button
        {...props}
        className={
            "slick-prev slick-arrow" +
            (currentSlide === 0 ? " slick-disabled" : "")
        }
        aria-disabled={currentSlide === 0 ? true : false}
        disabled={currentSlide === 0}
        type='button'
    >
        <LeftArrow width='24' height='24' />
    </button>
);

const SlickArrowRight = ({ currentSlide, slideCount, ...props }) => (
    <button
        {...props}
        className={
            "slick-next slick-arrow" +
            (currentSlide === slideCount - 1 ? " slick-disabled" : "")
        }
        aria-disabled={currentSlide === slideCount - 1 ? true : false}
        disabled={currentSlide === slideCount - 1}
        type='button'
    >
        <RightArrow width='24' height='24' />
    </button>
);

const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,

    responsive: [
        {
            breakpoint: 768,
            settings: {
                arrows: false
            }
        }
    ]
};

const similarItemsSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,
    centerPadding: "160px",
    responsive: [
        {
            breakpoint: 992,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1
            }
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2
            }
        },
        {
            breakpoint: 550,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1
            }
        }
    ]
};

const Product = () => {
    const params = useParams();
    const productId = params.id;

    const {
        similar,
        cart,
        customerId,
        product,
        updating,
        firstLogin,
        setFirstLogin,
        setCartOpen,
        setUpdating,
        getProduct,
        addCart,
        updateCart,
        logout
    } = useBetween(useApi);

    const [qty, setQty] = useState(1);
    const [cartQty, setCartQty] = useState(0);
    // displayQty is used for Optimistic UI feedback
    const [displayQty, setDisplayQty] = useState(0);
    const [status, setStatus] = useState(false);
    const [orderCreated, setOrderCreated] = useState(null);
    const debounceRef = useRef(null);

    const handleCartAction = (item, newQuantity) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        setUpdating(true);
        setDisplayQty(newQuantity);

        debounceRef.current = setTimeout(() => {
            const isFirstLogin = firstLogin;
            const inventoryId = item.InventoryID?.value;

            if (Object.keys(cart).length) {
                const products = [...cart.products];
                const duplicatedId = products.findIndex(
                    (cartItem) => cartItem.productId === inventoryId
                );

                if (newQuantity <= 0) {
                    if (duplicatedId >= 0) products.splice(duplicatedId, 1);
                } else {
                    if (duplicatedId >= 0) {
                        products[duplicatedId] = { ...products[duplicatedId], quantity: newQuantity };
                    } else {
                        products.push({ productId: inventoryId, quantity: newQuantity });
                    }
                }

                const formData = {
                    sessionId: cart.customerId,
                    customerId: cart.customerId,
                    products
                };

                updateCart(cart._id, formData)
                    .finally(() => setUpdating(false))
                    .catch((err) => {
                        toast.error(err.message);
                        setDisplayQty(cartQty); // Revert on error
                    });
            } else if (newQuantity > 0) {
                const formData = {
                    sessionId: customerId,
                    customerId: customerId,
                    products: [{ productId: inventoryId, quantity: newQuantity }]
                };

                addCart(formData)
                    .finally(() => setUpdating(false))
                    .catch((err) => {
                        toast.error(err.message);
                        setDisplayQty(0); // Revert on error
                    });
            } else {
                setUpdating(false);
            }

            if (isFirstLogin) {
                setCartOpen(true);
                setFirstLogin(false);
                cookieStorage.setItem("firstLogin", false);
            }
        }, 500); // 500ms debounce

    };

    const addToCart = (item, event) => {
        event.stopPropagation();
        const step = item.Casesize.value || 1;
        const currentTotal = displayQty || 0;
        const quantityToAdd = step <= 5 ? step : customOperation(step, currentTotal);
        handleCartAction(item, currentTotal + quantityToAdd);
    };

    const removeToCart = (item, event) => {
        event.stopPropagation();
        const step = item.Casesize.value || 1;
        const currentTotal = displayQty || 0;
        const quantityToRemove = step <= 5 ? step : customOperation(step, currentTotal);
        handleCartAction(item, Math.max(0, currentTotal - quantityToRemove));
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        setStatus(false);
        getProduct(productId)
            .then((res) => {
                setStatus(true);
                if (res.orderCreated) {
                    setOrderCreated(res.orderCreated);
                }
            })
            .catch(() => logout());
    }, [productId]);

    useEffect(() => {
        if (Object.keys(cart).length && product.InventoryID?.value) {
            const duplicatedId = cart.products.findIndex(
                (cartItem) => cartItem.productId === product.InventoryID.value
            );
            if (duplicatedId >= 0) {
                const q = cart.products[duplicatedId].quantity;
                setCartQty(q);
                setDisplayQty((prev) => (updating ? prev : q));
            } else {
                setCartQty(0);
                setDisplayQty((prev) => (updating ? prev : 0));
            }
        }
    }, [cart, product, updating]);

    useEffect(() => {
        if (product?.Casesize?.value) {
            setQty(product.Casesize.value);
        }
    }, [product?.Casesize?.value]);

    const similarProducts = Array.isArray(similar) ? similar : [];

    let content = null;
    if (!status) {
        content = <ProductSkeleton />;
    } else if (Object.keys(product).length) {
        content = (
                <div className='product-container'>
                    <div className='product-details'>
                        <Container>
                            <Row>
                                <Col md='6' className='product-img'>
                                    <div className='main-product-image'>
                                        {product.Label?.value ? (
                                            <span className='discount-badge'>
                                                {product.Label?.value}
                                            </span>
                                        ) : null}
                                        {product.FROZEN_PRODUCT?.value ? (
                                            <span className='frozen-badge'>
                                                Frozen
                                            </span>
                                        ) : null}
                                        <Slider {...settings}>
                                            <div className='img-wrapper'>
                                                {product
                                                    .Primaryproductimagefirstdisplayed
                                                    .value ? (
                                                    <Image
                                                        src={
                                                            product
                                                                .Primaryproductimagefirstdisplayed
                                                                .value
                                                        }
                                                        alt='product'
                                                    />
                                                ) : (
                                                    <Image
                                                        src={ProductImg}
                                                        alt='product'
                                                    />
                                                )}
                                            </div>
                                        </Slider>
                                    </div>
                                    <div className='similar-items'>
                                        <h5>Similar Products</h5>
                                        <div className='similar-wrapper'>
                                            <Slider {...similarItemsSettings}>
                                                {similarProducts.map((item, idx) => {
                                                    const similarProductImageSrc =
                                                        item
                                                            .Primaryproductimagefirstdisplayed
                                                            .value || ProductImg

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className='similar-product-item '
                                                        >
                                                        <div className='similar-cell d-flex'>
                                                            <Link
                                                                className='d-flex'
                                                                to={`/product/${item.InventoryID?.value}`}
                                                            >
                                                                <div className='img-wrapper'>
                                                                    {item.Label?.value ? (
                                                                        <span className='discount-badge'>
                                                                            {item.Label?.value}
                                                                        </span>
                                                                    ) : null}
                                                                    {item.FROZEN_PRODUCT?.value ? (
                                                                        <span className='frozen-badge'>
                                                                            Frozen
                                                                        </span>
                                                                    ) : null}
                                                                    <Image
                                                                        src={
                                                                            similarProductImageSrc
                                                                        }
                                                                        alt='product'
                                                                    />
                                                                </div>
                                                            </Link>
                                                            <div className='d-flex align-items-center justify-content-between'>
                                                                <Link
                                                                    className='d-flex'
                                                                    to={`/product/${item.InventoryID?.value}`}
                                                                >
                                                                    <div className='d-flex flex-column'>
                                                                        <h4>
                                                                            {
                                                                                item
                                                                                    .Buyermenuproductname
                                                                                    .value
                                                                            }
                                                                        </h4>
                                                                        <label>
                                                                            {
                                                                                item
                                                                                    .StrainDescription
                                                                                    .value
                                                                            }
                                                                            {item.Phenotype ? (
                                                                                <span
                                                                                    className='green-tg'
                                                                                    data-phenotype={
                                                                                        item.Phenotype
                                                                                    }
                                                                                >
                                                                                    <span>
                                                                                        {item.Phenotype.charAt(
                                                                                            0
                                                                                        ).toUpperCase()}
                                                                                    </span>
                                                                                </span>
                                                                            ) : null}
                                                                        </label>
                                                                        <p>
                                                                            {item?.OriginalPrice?.value &&
                                                                                item?.DefaultPrice?.value &&
                                                                                item.OriginalPrice.value !== 0 &&
                                                                                item.DefaultPrice.value < item.OriginalPrice.value ? (
                                                                                <span className='origin me-1'>
                                                                                    ${toTwoDecimals(item.OriginalPrice.value)}
                                                                                </span>
                                                                            ) : null}
                                                                            <span>
                                                                                ${toTwoDecimals(item?.DefaultPrice?.value || 0)}
                                                                            </span>
                                                                            {item
                                                                                .Casesize
                                                                                .value >
                                                                                1 ? (
                                                                                <span className='price-amount'>{` (${item.Casesize.value} units / case)`}</span>
                                                                            ) : null}
                                                                        </p>
                                                                    </div>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                        </div>
                                                    )
                                                })}
                                            </Slider>
                                        </div>
                                    </div>
                                </Col>
                                <Col md='6' className='ps-md-0'>
                                    <div className='product-detail-desp-box'>
                                        <div className='sub-title mb-2'>
                                            {`${product.Brand.value} | ${product.Buyermenuproductname.value}`}
                                        </div>
                                        <div className='title mb-3'>
                                            <h1>
                                                {
                                                    product.StrainDescription
                                                        .value
                                                }
                                            </h1>
                                        </div>
                                        <div className='d-flex mb-4'>
                                            {product.Phenotype ? (
                                                <span
                                                    className='tag tag-green mr-2'
                                                    data-phenotype={
                                                        product.Phenotype
                                                    }
                                                >
                                                    {product.Phenotype}
                                                </span>
                                            ) : null}

                                            {product.Displaypotency.value ===
                                                "Yes" ? (
                                                <span className='thc tag mx-2'>
                                                    {product.minTHC ===
                                                        product.maxTHC
                                                        ? `${roundDecimals(
                                                            product.THC.value
                                                        )}% `
                                                        : `${roundDecimals(
                                                            product.minTHC
                                                        )}% ~ ${roundDecimals(
                                                            product.maxTHC
                                                        )}% `}
                                                    THC
                                                </span>
                                            ) : null}
                                        </div>

                                        <div className='product-quantity-in-stock'>
                                        </div>

                                        <div className='price'>
                                            <div className='price-info-block'>
                                                <span className=''>
                                                    <span className='price-unit'>
                                                        {product?.OriginalPrice?.value &&
                                                            product?.DefaultPrice?.value &&
                                                            product.OriginalPrice.value !== 0 &&
                                                            product.DefaultPrice.value < product.OriginalPrice.value ? (
                                                            <span className='origin me-2'>
                                                                ${toTwoDecimals(product.OriginalPrice.value)}
                                                            </span>
                                                        ) : null}
                                                        ${toTwoDecimals(product?.DefaultPrice?.value || 0)}
                                                    </span>
                                                    <span className='price-total'>
                                                        {`$${toTwoDecimals(
                                                            (product?.DefaultPrice?.value || 0) * product.Casesize.value
                                                        )} / ${product.Casesize.value > 1 ? "Case" : "Unit"}`}
                                                    </span>
                                                </span>
                                                {product.Casesize.value > 1 ? (
                                                    <span className='price-amount'>{`(${product.Casesize.value} units)`}</span>
                                                ) : null}
                                            </div>
                                            {orderCreated ? (
                                                <div className='last-purchase'>
                                                    Last purchased on{" "}
                                                    {toDateType(orderCreated)}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className='qty-container mb-3'>
                                            <div className='product-action-button-wrapper mb-4'>
                                                {displayQty > 0 ? (
                                                    <span className='plus-minus'>
                                                        <button
                                                            type='button'
                                                            aria-label='Decrease quantity'
                                                            className='d-flex align-items-center cursor-pointer justify-content-center control-icon'
                                                            onClick={(e) =>
                                                                removeToCart(
                                                                    product,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            -
                                                        </button>
                                                        <span className='quantity-block'>
                                                            {displayQty}
                                                        </span>
                                                        <button
                                                            type='button'
                                                            aria-label='Increase quantity'
                                                            className='d-flex align-items-center cursor-pointer justify-content-center control-icon'
                                                            onClick={(e) =>
                                                                addToCart(
                                                                    product,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            +
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <AwareButton
                                                        className='btn-atc btn-green primary'
                                                        onClick={(e) =>
                                                            addToCart(
                                                                product,
                                                                e
                                                            )
                                                        }
                                                    >
                                                        Add to Cart
                                                    </AwareButton>
                                                )}
                                            </div>
                                        </div>

                                        <div className='desc-table'>
                                            {product.Description.value ? (
                                                <div className='description'>
                                                    <p>Product Description</p>
                                                    <div
                                                        className='description-content'
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                product
                                                                    .Description
                                                                    .value
                                                        }}
                                                    ></div>
                                                </div>
                                            ) : null}

                                            {product.Taste.length ? (
                                                <div className='description description-limit flex-column d-flex justify-content-between'>
                                                    <p>Taste</p>
                                                    <label>
                                                        {product.Taste.join(
                                                            ", "
                                                        )}
                                                    </label>
                                                </div>
                                            ) : null}
                                            {product.Effects.length ? (
                                                <div className='description description-limit flex-column d-flex justify-content-between'>
                                                    <p>Effects</p>
                                                    <label>
                                                        {product.Effects.join(
                                                            ", "
                                                        )}
                                                    </label>
                                                </div>
                                            ) : null}
                                            {product.Terpenes.length ? (
                                                <div className='description description-limit flex-column d-flex justify-content-between'>
                                                    <p>Terpenes</p>
                                                    <label>
                                                        {product.Terpenes.join(
                                                            ", "
                                                        )}
                                                    </label>
                                                </div>
                                            ) : null}
                                            {product.Lineage ? (
                                                <div className='description description-limit flex-column d-flex justify-content-between'>
                                                    <p>Lineage</p>
                                                    <label>
                                                        {product.Lineage}
                                                    </label>
                                                </div>
                                            ) : null}
                                            {product?.BFLink?.value && (
                                                <div className='description'>
                                                    <p>Marketing Assets</p>
                                                    <div className='resources-buttons'>
                                                        <div className=''>
                                                            <AwareButton
                                                                className='aware-button primary btn-review btn-atc primary'
                                                                href={
                                                                    product
                                                                        ?.BFLink
                                                                        ?.value
                                                                }
                                                                target='_blank'
                                                                download={
                                                                    product
                                                                        ?.StrainDescription
                                                                        ?.value
                                                                }
                                                            >
                                                                Download Assets
                                                            </AwareButton>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </div>
        );
    }

    return (
        <CartLayout>
            {content}
            {/* {updating ? (
                <div className='update-loading'>
                    <Spinner animation='border' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                    </Spinner>
                </div>
            ) : null} */}
        </CartLayout>
    );
};

export default Product;
